import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import { Alert } from '@material-ui/lab';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { openChoreoClientApiRef } from '../../api/OpenChoreoClientApi';
import { makeStyles } from '@material-ui/core/styles';
import { isForbiddenError, getErrorMessage } from '../../utils/errorUtils';
import { OverrideContent } from './OverrideContent';
import { useOverrideChanges } from './hooks/useOverrideChanges';
import { useOverridesData } from './hooks/useOverridesData';
import { ComponentReleaseDiffDialog } from './components/ComponentReleaseDiffDialog';
import { SaveConfirmationDialog } from './SaveConfirmationDialog';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import {
  UnsavedChangesDialog,
  useUrlSyncedTab,
  DetailPageLayout,
  useDeployPermission,
} from '@openchoreo/backstage-plugin-react';
import {
  calculateHasOverrides,
  getMissingRequiredFields,
} from './overridesUtils';
import type { Environment } from './hooks/useEnvironmentData';
import type { PendingAction } from './types';
import {
  VerticalTabNav,
  TabItemData,
} from '@openchoreo/backstage-design-system';
import MemoryIcon from '@material-ui/icons/Memory';
import SettingsIcon from '@material-ui/icons/Settings';
import ExtensionIcon from '@material-ui/icons/Extension';
import { ContainerContent } from './Workload/WorkloadEditor';
import {
  useSecretReferences,
  filterSecretReferencesForEnvDataPlane,
} from '@openchoreo/backstage-plugin-react';
import type { EnvVar } from '@openchoreo/backstage-plugin-common';
import { TraitParameters } from './TraitParameters';

const useStyles = makeStyles(theme => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    gap: theme.spacing(2),
  },
  helpText: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  tabNav: {
    height: '100%',
    minHeight: 400,
  },
}));

interface EnvironmentOverridesPageProps {
  environment: Environment;
  entity: Entity;
  onBack: () => void;
  onSaved: () => void;
  /** Pending action to execute after saving overrides */
  pendingAction?: PendingAction;
  /** Callback when overrides are saved and pending action should be executed */
  onPendingActionComplete?: (action: PendingAction) => Promise<void>;
  /** Callback when Previous button is clicked (only shown when pendingAction exists) */
  onPrevious?: () => void;
  /** Initial tab to display (from URL) */
  initialTab?: string;
  /** Callback when tab changes (to update URL). Second param is replace (default false). */
  onTabChange?: (tabId: string, replace?: boolean) => void;
}

export const EnvironmentOverridesPage = ({
  environment,
  entity,
  onBack,
  onSaved,
  pendingAction,
  onPendingActionComplete,
  onPrevious,
  initialTab,
  onTabChange,
}: EnvironmentOverridesPageProps) => {
  const classes = useStyles();
  const client = useApi(openChoreoClientApiRef);

  const [traitTypeMap, setTraitTypeMap] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    'all' | 'component' | string | null
  >(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);

  // Check deploy permission for the final Deploy/Promote button. Pass the
  // target environment so ABAC `resource.environment` CEL constraints are
  // honored (openchoreo#3408).
  const environmentNameForPermission =
    environment.resourceName || environment.name;
  const { canDeploy, loading: deployPermissionLoading } = useDeployPermission(
    environmentNameForPermission,
  );

  // Load secret references for workload overrides — limited to refs whose
  // value is reachable from this environment's data plane (or unscoped refs).
  const { secretReferences: allSecretReferences } = useSecretReferences();
  const secretReferences = useMemo(
    () =>
      filterSecretReferencesForEnvDataPlane(allSecretReferences, {
        kind: environment.dataPlaneKind,
        name: environment.dataPlaneRef,
      }),
    [allSecretReferences, environment.dataPlaneKind, environment.dataPlaneRef],
  );

  // Fetch component traits to get trait type information
  useEffect(() => {
    const fetchTraitTypes = async () => {
      try {
        const traits = await client.fetchComponentTraits(entity);
        const typeMap: Record<string, string> = {};
        traits.forEach(trait => {
          typeMap[trait.instanceName] = trait.name;
        });
        setTraitTypeMap(typeMap);
      } catch (err) {
        // Silently fail - trait types are nice to have but not critical
      }
    };
    fetchTraitTypes();
  }, [client, entity]);

  // Use pendingAction release name when promoting/deploying, otherwise use environment's release
  const releaseNameForOverrides =
    pendingAction?.releaseName || environment.deployment.releaseName;

  // Use K8s resource name for API calls (matches binding.environment from backend)
  const environmentName = environment.resourceName || environment.name;

  // Load data using custom hook
  const {
    loading,
    error: loadError,
    schemas,
    formState,
    setComponentTypeFormData,
    setTraitFormDataMap,
    setWorkloadFormData,
    reload,
  } = useOverridesData(
    entity,
    environmentName,
    releaseNameForOverrides,
    true, // always open
  );

  const error = loadError || saveError;

  // Calculate changes
  const changes = useOverrideChanges(
    formState.initialComponentTypeFormData,
    formState.componentTypeFormData,
    formState.initialTraitFormDataMap,
    formState.traitFormDataMap,
    formState.initialWorkloadFormData,
    formState.workloadFormData,
  );

  // Calculate override states
  const currentOverrides = calculateHasOverrides(
    formState.componentTypeFormData,
    formState.traitFormDataMap,
    formState.workloadFormData,
  );
  const initialOverrides = calculateHasOverrides(
    formState.initialComponentTypeFormData,
    formState.initialTraitFormDataMap,
    formState.initialWorkloadFormData,
  );

  // Calculate missing required fields for validation
  const missingRequiredFields = useMemo(() => {
    if (!schemas.componentTypeSchema) {
      return [];
    }
    return getMissingRequiredFields(
      schemas.componentTypeSchema,
      formState.componentTypeFormData,
    );
  }, [schemas.componentTypeSchema, formState.componentTypeFormData]);

  // Helper function to determine tab status with priority: error > info > success > undefined
  const getTabStatus = (
    hasInitialData: boolean,
    hasChanges: boolean,
    hasMissingRequired: boolean,
  ): TabItemData['status'] => {
    if (hasMissingRequired) return 'error';
    if (hasChanges) return 'info';
    if (hasInitialData) return 'success';
    return undefined;
  };

  // Build tabs from available schemas
  const tabs = useMemo<TabItemData[]>(() => {
    const tabList: TabItemData[] = [];

    // Add Workload tab first (matches Configure Component order)
    // Use hasActualWorkloadOverrides to check if backend has real overrides
    const hasInitialWorkloadData = formState.hasActualWorkloadOverrides;
    const hasWorkloadChanges = (changes.workload?.length || 0) > 0;
    // Workload doesn't use JSON schema, so no required field validation
    tabList.push({
      id: 'workload',
      label: 'Workload',
      icon: <SettingsIcon />,
      status: getTabStatus(hasInitialWorkloadData, hasWorkloadChanges, false),
    });

    // Add Component Overrides tab if schema exists
    if (schemas.componentTypeSchema) {
      // Use hasActualComponentOverrides to check if backend has real overrides (not just defaults)
      const hasInitialComponentData = formState.hasActualComponentOverrides;
      const hasComponentChanges = changes.component.length > 0;
      const hasMissingComponentRequired = missingRequiredFields.length > 0;

      tabList.push({
        id: 'component',
        label: 'Component',
        icon: <MemoryIcon />,
        status: getTabStatus(
          hasInitialComponentData,
          hasComponentChanges,
          hasMissingComponentRequired,
        ),
      });
    }

    // Build trait child tabs
    const traitTabs: TabItemData[] = [];
    Object.keys(schemas.traitSchemasMap).forEach(traitName => {
      const traitSchema = schemas.traitSchemasMap[traitName];
      // Use hasActualTraitEnvironmentConfigsMap to check if backend has real overrides for this trait
      const hasInitialTraitData =
        formState.hasActualTraitEnvironmentConfigsMap[traitName] || false;
      const hasTraitChanges = (changes.traits[traitName]?.length || 0) > 0;
      const traitMissingRequired = getMissingRequiredFields(
        traitSchema,
        formState.traitFormDataMap[traitName],
      );

      traitTabs.push({
        id: `trait-${traitName}`,
        label: traitName,
        // icon: <ExtensionIcon />,
        status: getTabStatus(
          hasInitialTraitData,
          hasTraitChanges,
          traitMissingRequired.length > 0,
        ),
      });
    });

    // Add Traits group if there are any trait tabs
    if (traitTabs.length > 0) {
      // Aggregate status from child traits - prioritize error > info > success
      let aggregatedStatus: TabItemData['status'] = undefined;
      if (traitTabs.some(t => t.status === 'error')) {
        aggregatedStatus = 'error';
      } else if (traitTabs.some(t => t.status === 'info')) {
        aggregatedStatus = 'info';
      } else if (traitTabs.some(t => t.status === 'success')) {
        aggregatedStatus = 'success';
      }

      tabList.push({
        id: 'traits-group',
        label: 'Traits',
        icon: <ExtensionIcon />,
        isGroup: true,
        children: traitTabs,
        status: aggregatedStatus,
      });
    }

    return tabList;
  }, [
    schemas,
    formState.hasActualComponentOverrides,
    formState.hasActualTraitEnvironmentConfigsMap,
    formState.hasActualWorkloadOverrides,
    formState.traitFormDataMap,
    changes,
    missingRequiredFields,
  ]);

  // Tab state with URL sync
  const [activeTab, setActiveTab] = useUrlSyncedTab({
    initialTab,
    defaultTab: '',
    onTabChange,
  });

  // Set default active tab when tabs are loaded
  useEffect(() => {
    // baseWorkloadData is only set after initial load completes (null until then)
    if (
      !loading &&
      formState.baseWorkloadData &&
      tabs.length > 0 &&
      !activeTab
    ) {
      const defaultTab = tabs[0].id;
      // Use replace: true to avoid adding to history when auto-setting default tab
      setActiveTab(defaultTab, true);
    }
  }, [loading, formState.baseWorkloadData, tabs, activeTab, setActiveTab]);

  // Workload container management functions.
  const handleContainerChange = (field: string, value: any) => {
    setWorkloadFormData((prev: any) => ({
      ...prev,
      container: { ...(prev.container || {}), [field]: value },
    }));
  };

  const handleEnvVarChange = (
    envIndex: number,
    field: string,
    value: string,
  ) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const env = [...(container.env || [])];
      env[envIndex] = { ...env[envIndex], [field]: value };
      return { ...prev, container: { ...container, env } };
    });
  };

  const handleEnvVarReplace = (envIndex: number, envVar: EnvVar) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const env = [...(container.env || [])];
      env[envIndex] = envVar;
      return { ...prev, container: { ...container, env } };
    });
  };

  const handleFileVarChange = (
    fileIndex: number,
    field: string,
    value: string,
  ) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const files = [...((container.files as any[]) || [])];
      files[fileIndex] = { ...files[fileIndex], [field]: value };
      return { ...prev, container: { ...container, files } };
    });
  };

  const handleAddEnvVar = () => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      return {
        ...prev,
        container: {
          ...container,
          env: [...(container.env || []), { key: '', value: '' }],
        },
      };
    });
  };

  const handleRemoveEnvVar = (envIndex: number) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const env = (container.env || []).filter(
        (_: any, index: number) => index !== envIndex,
      );
      return { ...prev, container: { ...container, env } };
    });
  };

  const handleAddFileVar = () => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      return {
        ...prev,
        container: {
          ...container,
          files: [
            ...((container.files as any[]) || []),
            { key: '', mountPath: '', value: '' },
          ],
        },
      };
    });
  };

  const handleRemoveFileVar = (fileIndex: number) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const files = ((container.files as any[]) || []).filter(
        (_: any, index: number) => index !== fileIndex,
      );
      return { ...prev, container: { ...container, files } };
    });
  };

  const handleArrayFieldChange = (field: string, value: string) => {
    const arrayValue = value
      .split(',')
      .map((item: string) => item.trim())
      .filter((item: string) => item);
    setWorkloadFormData((prev: any) => ({
      ...prev,
      container: { ...(prev.container || {}), [field]: arrayValue },
    }));
  };

  // Handle starting override of an inherited env var
  const handleStartOverride = (envVar: EnvVar) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const existingEnv: EnvVar[] = container.env || [];
      if (existingEnv.some((e: EnvVar) => e.key === envVar.key)) return prev;
      return {
        ...prev,
        container: { ...container, env: [...existingEnv, { ...envVar }] },
      };
    });
  };

  // Handle starting override of an inherited file var
  const handleStartFileOverride = (
    fileVar: import('@openchoreo/backstage-plugin-common').FileVar,
  ) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const existingFiles: any[] = (container.files as any[]) || [];
      if (
        existingFiles.some(
          (f: import('@openchoreo/backstage-plugin-common').FileVar) =>
            f.key === fileVar.key,
        )
      ) {
        return prev;
      }
      return {
        ...prev,
        container: { ...container, files: [...existingFiles, { ...fileVar }] },
      };
    });
  };

  // Handle replacing an entire file var at once (atomic update)
  const handleFileVarReplace = (
    fileIndex: number,
    fileVar: import('@openchoreo/backstage-plugin-common').FileVar,
  ) => {
    setWorkloadFormData((prev: any) => {
      const container = prev.container || {};
      const files = [...((container.files as any[]) || [])];
      files[fileIndex] = fileVar;
      return { ...prev, container: { ...container, files } };
    });
  };

  // Calculate if there are any changes
  const totalChanges =
    changes.component.length +
    Object.values(changes.traits).reduce(
      (sum, traitChanges) => sum + traitChanges.length,
      0,
    ) +
    (changes.workload?.length || 0);

  const handleSaveClick = async () => {
    // Allow skip when pendingAction exists but no changes
    if (totalChanges === 0 && pendingAction && onPendingActionComplete) {
      setSaving(true);
      try {
        // No override changes — just ensure binding exists with correct releaseName
        await client.updateReleaseBinding(
          entity,
          environmentName,
          pendingAction.releaseName,
        );
        await onPendingActionComplete(pendingAction);
      } catch (err) {
        setSaveError(
          isForbiddenError(err)
            ? 'You do not have permission to perform this action. Contact your administrator.'
            : getErrorMessage(err),
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    if (totalChanges === 0) {
      setSaveError('No changes to save');
      setTimeout(() => setSaveError(null), 3000);
      return;
    }
    setShowSaveConfirm(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      if (pendingAction) {
        // updateReleaseBinding handles create-or-update + sets releaseName
        // This is the ONLY call needed — replaces both patchReleaseBindingOverrides and deploy/promote
        await client.updateReleaseBinding(
          entity,
          environmentName,
          pendingAction.releaseName,
          formState.componentTypeFormData,
          formState.traitFormDataMap,
          formState.workloadFormData,
        );
      } else {
        // Plain save — use existing patchReleaseBindingOverrides
        await client.patchReleaseBindingOverrides(
          entity,
          environmentName,
          formState.componentTypeFormData,
          formState.traitFormDataMap,
          formState.workloadFormData,
        );
      }

      setShowSaveConfirm(false);

      // If there's a pending action, notify and navigate
      if (pendingAction && onPendingActionComplete) {
        await onPendingActionComplete(pendingAction);
        // onPendingActionComplete handles navigation
      } else {
        onSaved();
        onBack();
      }
    } catch (err) {
      setSaveError(
        isForbiddenError(err)
          ? 'You do not have permission to perform this action. Contact your administrator.'
          : getErrorMessage(err),
      );
      setShowSaveConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (target: 'all' | 'component' | string) => {
    setDeleteTarget(target);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setSaveError(null);

    try {
      if (deleteTarget === 'all') {
        await client.patchReleaseBindingOverrides(
          entity,
          environmentName,
          {},
          {},
          {},
        );
        setShowDeleteConfirm(false);
        onSaved();
        onBack();
      } else if (deleteTarget === 'component') {
        setComponentTypeFormData({});
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      } else if (deleteTarget === 'workload') {
        setWorkloadFormData({});
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      } else {
        // Delete specific trait overrides
        setTraitFormDataMap(prev => {
          const newMap = { ...prev };
          delete newMap[deleteTarget];
          return newMap;
        });
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      }
    } catch (err) {
      setSaveError(
        isForbiddenError(err)
          ? 'You do not have permission to perform this action. Contact your administrator.'
          : getErrorMessage(err),
      );
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const hasSchemas =
    schemas.componentTypeSchema ||
    Object.keys(schemas.traitSchemasMap).length > 0;

  // Workload overrides are always available for deployed components,
  // so we always have content to show even without schema overrides.
  const hasAnyContent = hasSchemas || tabs.length > 0;

  // Handle back button click with unsaved changes warning
  const handleBackClick = () => {
    if (totalChanges > 0) {
      setShowUnsavedChangesDialog(true);
    } else {
      onBack();
    }
  };

  // Determine button text based on pending action and changes
  const getSaveButtonText = () => {
    if (saving) {
      if (pendingAction?.type === 'deploy') return 'Deploying...';
      if (pendingAction?.type === 'promote') return 'Promoting...';
      return 'Saving...';
    }

    if (pendingAction) {
      const hasChanges = totalChanges > 0;
      if (pendingAction.type === 'deploy') {
        return hasChanges ? 'Save & Deploy' : 'Deploy';
      }
      if (pendingAction.type === 'promote') {
        return hasChanges ? 'Save & Promote' : 'Promote';
      }
    }

    return 'Save Overrides';
  };

  // Get the warning message for missing required fields
  const getRequiredFieldsWarningMessage = () => {
    if (!pendingAction) {
      return 'Fill in the required fields below before saving.';
    }
    if (pendingAction.type === 'deploy') {
      return `Fill in the required fields below before deploying to ${pendingAction.targetEnvironment}.`;
    }
    return `Fill in the required fields below before promoting to ${pendingAction.targetEnvironment}.`;
  };

  // Header actions - show when content exists OR when there's a pending action (for Skip & Deploy)
  const showActions = !loading && !error && (hasAnyContent || pendingAction);
  const headerActions = showActions ? (
    <>
      {!pendingAction && (
        <Button
          onClick={() => handleDeleteClick('all')}
          color="secondary"
          disabled={deleting || saving || loading || !initialOverrides.hasAny}
        >
          Delete All
        </Button>
      )}
      {pendingAction?.type === 'promote' && pendingAction.releaseName && (
        <Tooltip title="Compare the source release with this environment's current release">
          <span>
            <Button
              onClick={() => setDiffOpen(true)}
              variant="outlined"
              startIcon={<CompareArrowsIcon fontSize="small" />}
              disabled={saving || deleting}
              style={{ marginRight: 8 }}
            >
              View release diff
            </Button>
          </span>
        </Tooltip>
      )}
      {pendingAction && onPrevious && (
        <Button
          onClick={onPrevious}
          variant="outlined"
          disabled={saving || deleting}
          style={{ marginRight: 8 }}
        >
          Previous
        </Button>
      )}
      <Button
        onClick={handleSaveClick}
        variant="contained"
        color="primary"
        disabled={
          saving ||
          deleting ||
          loading ||
          !!error ||
          missingRequiredFields.length > 0 ||
          (!pendingAction && totalChanges === 0) ||
          (!!pendingAction && (deployPermissionLoading || !canDeploy))
        }
      >
        {getSaveButtonText()}
      </Button>
    </>
  ) : null;

  // Render content for active tab
  const renderTabContent = () => {
    if (activeTab === 'component' && schemas.componentTypeSchema) {
      return (
        <OverrideContent
          title="Component Overrides"
          schema={schemas.componentTypeSchema}
          formData={formState.componentTypeFormData}
          onChange={setComponentTypeFormData}
          onDelete={() => handleDeleteClick('component')}
          hasInitialData={initialOverrides.hasComponentOverrides}
          disabled={saving || deleting}
          showValidation
        />
      );
    }

    // Check for trait tabs
    if (activeTab.startsWith('trait-')) {
      const traitName = activeTab.replace('trait-', '');
      const traitSchema = schemas.traitSchemasMap[traitName];
      if (traitSchema) {
        // Get trait type from the map, fallback to generic "Trait" if not found
        const traitType = traitTypeMap[traitName] || 'trait';

        return (
          <OverrideContent
            title={`${traitName} Trait`}
            contentTitle={
              <>
                <Box display="flex" alignItems="center" gridGap={4} mb={4}>
                  <Typography variant="h4">{traitName}</Typography>
                  <Typography variant="h4" color="textSecondary">
                    ({traitType})
                  </Typography>
                </Box>
                <TraitParameters
                  entity={entity}
                  traitInstanceName={traitName}
                />
              </>
            }
            sectionTitle={
              <Box mb={2}>
                <Typography variant="h5" gutterBottom>
                  Overrides
                </Typography>
              </Box>
            }
            schema={traitSchema}
            formData={formState.traitFormDataMap[traitName] || {}}
            onChange={newData =>
              setTraitFormDataMap(prev => ({
                ...prev,
                [traitName]: newData,
              }))
            }
            onDelete={() => handleDeleteClick(traitName)}
            hasInitialData={
              !!formState.initialTraitFormDataMap[traitName] &&
              Object.keys(formState.initialTraitFormDataMap[traitName]).length >
                0
            }
            disabled={saving || deleting}
            showValidation
          />
        );
      }
    }

    if (activeTab === 'workload') {
      return (
        <OverrideContent
          title="Workload Overrides"
          schema={null} // No JSON schema for workload overrides
          formData={formState.workloadFormData}
          onChange={setWorkloadFormData}
          onDelete={() => handleDeleteClick('workload')}
          hasInitialData={initialOverrides.hasWorkloadOverrides}
          wrapCustomContent
          sectionTitle={
            <Box mb={3}>
              <Typography variant="h4">Container</Typography>
            </Box>
          }
          customContent={
            <ContainerContent
              container={formState.workloadFormData.container}
              onContainerChange={handleContainerChange}
              onEnvVarChange={handleEnvVarChange}
              onEnvVarReplace={handleEnvVarReplace}
              onFileVarChange={handleFileVarChange}
              onFileVarReplace={handleFileVarReplace}
              onAddEnvVar={handleAddEnvVar}
              onRemoveEnvVar={handleRemoveEnvVar}
              onAddFileVar={handleAddFileVar}
              onRemoveFileVar={handleRemoveFileVar}
              onArrayFieldChange={handleArrayFieldChange}
              disabled={saving || deleting || loading}
              hideContainerFields
              secretReferences={secretReferences}
              baseWorkloadData={formState.baseWorkloadData}
              showEnvVarStatus
              onStartOverride={handleStartOverride}
              onStartFileOverride={handleStartFileOverride}
              environmentName={environment.name}
            />
          }
        />
      );
    }

    return null;
  };

  return (
    <>
      <DetailPageLayout
        title={
          pendingAction && missingRequiredFields.length > 0
            ? 'Configure Required Environment Overrides'
            : 'Configure Environment Overrides'
        }
        subtitle={environment.name}
        onBack={handleBackClick}
        actions={headerActions}
      >
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}

        {error && !loading && (
          <div className={classes.errorContainer}>
            <Typography color="error">{error}</Typography>
            <Button onClick={reload} variant="outlined">
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && !hasAnyContent && pendingAction && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={300}
            p={4}
          >
            <Typography variant="h6" gutterBottom>
              No Configuration Required
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ marginBottom: 24 }}
            >
              This component has no environment-specific overrides to configure.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveClick}
              disabled={saving || deployPermissionLoading || !canDeploy}
            >
              {getSaveButtonText()}
            </Button>
          </Box>
        )}

        {!loading && !error && hasAnyContent && (
          <>
            {missingRequiredFields.length > 0 && (
              <Box mb={2}>
                <Alert severity="warning">
                  <strong>Required configuration needed.</strong>{' '}
                  {getRequiredFieldsWarningMessage()}
                </Alert>
              </Box>
            )}

            {!currentOverrides.hasAny && !pendingAction && (
              <Box className={classes.helpText}>
                <Typography variant="body2" gutterBottom>
                  <strong>Environment Overrides</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Configure environment-specific settings for your component's
                  containers, such as environment variables and file mounts.
                  These overrides apply only to the{' '}
                  <strong>{environment.name}</strong> environment.
                </Typography>
              </Box>
            )}

            {tabs.length > 0 && (
              <VerticalTabNav
                tabs={tabs}
                activeTabId={activeTab}
                onChange={setActiveTab}
                className={classes.tabNav}
              >
                {renderTabContent()}
              </VerticalTabNav>
            )}

            {tabs.length === 0 && (
              <Box
                p={2}
                style={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No configuration options available for this component.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DetailPageLayout>

      <SaveConfirmationDialog
        open={showSaveConfirm}
        onCancel={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
        changes={changes}
        environmentName={environment.name}
        saving={saving}
      />

      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        deleteTarget={deleteTarget}
        initialComponentTypeFormData={formState.initialComponentTypeFormData}
        initialTraitFormDataMap={formState.initialTraitFormDataMap}
        deleting={deleting}
      />

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onDiscard={() => {
          setShowUnsavedChangesDialog(false);
          onBack();
        }}
        onStay={() => setShowUnsavedChangesDialog(false)}
        changeCount={totalChanges}
      />

      {pendingAction?.type === 'promote' && pendingAction.releaseName && (
        <ComponentReleaseDiffDialog
          open={diffOpen}
          onClose={() => setDiffOpen(false)}
          upstreamEnvName={pendingAction.sourceEnvironment}
          upstreamReleaseName={pendingAction.releaseName}
          environmentName={environment.name}
          releaseName={environment.deployment.releaseName}
        />
      )}
    </>
  );
};
